// Utilitaires pour l'inscription aux activités

/**
 * Formate une date en format dd/mm/yyyy
 * @param {string} dateString - La date à formater
 * @returns {string} La date formatée
 */
export const formatDate = (dateString) => {
  // Vérification si la date est valide
  if (!dateString) {
    console.warn('formatDate: dateString is empty or undefined', dateString);
    return 'Date non disponible';
  }

  const date = new Date(dateString);
  
  // Vérification si la date est valide
  if (isNaN(date.getTime())) {
    console.warn('formatDate: Invalid date string', dateString);
    return 'Date invalide';
  }

  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

/**
 * Valide le format d'un email
 * @param {string} email - L'email à valider
 * @returns {boolean} True si l'email est valide
 */
export const isValidEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * Valide le format d'un numéro de téléphone
 * @param {string} phoneNumber - Le numéro à valider
 * @returns {boolean} True si le numéro est valide
 */
export const isValidPhoneNumber = (phoneNumber) => {
  return /^[0-9\s\-().]+$/.test(phoneNumber);
};

/**
 * Valide les données d'un accompagnateur
 * @param {Object} data - Les données à valider
 * @returns {Object} Objet contenant les erreurs de validation
 */
export const validateAccompanistData = (data) => {
  const errors = {};

  if (!data.firstName.trim()) {
    errors.firstName = 'Le prénom est requis';
  }

  if (!data.lastName.trim()) {
    errors.lastName = 'Le nom est requis';
  }

  if (!data.email.trim()) {
    errors.email = 'L\'email est requis';
  } else if (!isValidEmail(data.email)) {
    errors.email = 'Format d\'email invalide';
  }

  if (!data.phoneNumber.trim()) {
    errors.phoneNumber = 'Le numéro de téléphone est requis';
  } else if (!isValidPhoneNumber(data.phoneNumber)) {
    errors.phoneNumber = 'Format de numéro de téléphone invalide';
  }

  return errors;
};

/**
 * Valide les données d'email d'un futur soldat
 * @param {Object} data - Les données à valider
 * @returns {Object} Objet contenant les erreurs de validation
 */
export const validateSoldierEmailData = (data) => {
  const errors = {};

  if (!data.email.trim()) {
    errors.email = 'L\'email est requis';
  } else if (!isValidEmail(data.email)) {
    errors.email = 'Format d\'email invalide';
  }

  if (!data.confirmEmail.trim()) {
    errors.confirmEmail = 'La confirmation de l\'email est requise';
  } else if (data.email !== data.confirmEmail) {
    errors.confirmEmail = 'Les emails ne correspondent pas';
  }

  return errors;
};

/**
 * Valide les données supplémentaires d'un futur soldat
 * @param {Object} data - Les données à valider
 * @returns {Object} Objet contenant les erreurs de validation
 */
export const validateSoldierAdditionalData = (data) => {
  const errors = {};

  if (!data.firstName.trim()) {
    errors.firstName = 'Le prénom est requis';
  }

  if (!data.lastName.trim()) {
    errors.lastName = 'Le nom est requis';
  }

  if (!data.phoneNumber.trim()) {
    errors.phoneNumber = 'Le numéro de téléphone est requis';
  } else if (!isValidPhoneNumber(data.phoneNumber)) {
    errors.phoneNumber = 'Format de numéro de téléphone invalide';
  }

  return errors;
};

/**
 * Vérifie si les données d'accompagnateur sont valides
 * @param {Object} data - Les données à vérifier
 * @returns {boolean} True si toutes les données sont valides
 */
export const isAccompanistDataValid = (data) => {
  return Object.keys(validateAccompanistData(data)).length === 0;
};

/**
 * Vérifie si les données d'email du futur soldat sont valides
 * @param {Object} data - Les données à vérifier
 * @returns {boolean} True si toutes les données sont valides
 */
export const isSoldierEmailDataValid = (data) => {
  return Object.keys(validateSoldierEmailData(data)).length === 0;
};

/**
 * Vérifie si les données supplémentaires du futur soldat sont valides
 * @param {Object} data - Les données à vérifier
 * @returns {boolean} True si toutes les données sont valides
 */
export const isSoldierAdditionalDataValid = (data) => {
  return Object.keys(validateSoldierAdditionalData(data)).length === 0;
};

/**
 * Prépare les données d'un participant pour l'API
 * @param {Object} participant - Le participant
 * @param {string} selectedActivity - L'ID de l'activité sélectionnée
 * @returns {Object} Les données formatées pour l'API
 */
export const prepareParticipantDataForAPI = (participant, selectedActivity) => {
  return {
    activiteType: parseInt(selectedActivity),
    firstName: participant.firstName,
    lastName: participant.lastName,
    isFuturSoldier: participant.type === 'Futur soldat',
    phoneNumber: participant.phone || '',
    mail: participant.email,
    lead_id: participant.leadId || null,
    hasArrived: false
  };
};